import { DOCUMENT } from '@angular/common';
import { VERSION, inject, Injectable, InjectionToken } from '@angular/core';
import * as Sentry from '@sentry/angular';
import type { SeverityLevel, User } from '@sentry/angular';

export type AppLogLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

export type AppLoggerTagValue = string | number | boolean | null | undefined;

export interface AppLoggerUser {
  userId?: string;
  [key: string]: unknown;
}

export interface AppLogContext {
  tags?: Record<string, AppLoggerTagValue>;
  extra?: Record<string, unknown>;
  user?: AppLoggerUser;
  fingerprint?: string[];
}

export interface AppLoggerBuildInfo {
  buildId?: string;
  builtAt?: string;
  buildProfile?: string;
  packageVersion?: string;
  gitSha?: string;
  mainBundleFile?: string;
  mainBundleHash?: string;
}

export interface AppLoggerSentryConfig {
  dsn: string;
  enabled?: boolean;
  environment?: string;
  release?: string;
  tunnel?: string;
  tracesSampleRate?: number;
  sendDefaultPii?: boolean;
}

export interface AppLoggerStartupLogConfig {
  enabled?: boolean;
  buildInfoUrl?: string;
}

export interface AppLoggerConfig {
  sentry?: AppLoggerSentryConfig;
  captureGlobalErrors?: boolean;
  console?: boolean;
  eventCooldownMs?: number;
  minimumEventLevel?: AppLogLevel | 'off';
  startupLog?: AppLoggerStartupLogConfig;
}

export type AppLoggerConfigSource = AppLoggerConfig | (() => AppLoggerConfig | Promise<AppLoggerConfig>);

export const APP_LOGGER_CONFIG = new InjectionToken<AppLoggerConfig>('APP_LOGGER_CONFIG');

const LOG_LEVEL_PRIORITY: Record<AppLogLevel, number> = {
  debug: 10,
  info: 20,
  warning: 30,
  error: 40,
  fatal: 50,
};
const BASE_CONTEXT_STARTUP_TIMEOUT_MS = 1500;
const BUILD_INFO_TIMEOUT_MS = 1500;

interface HighEntropyValues {
  architecture?: string;
  bitness?: string;
  fullVersionList?: Array<{ brand: string; version: string }>;
  mobile?: boolean;
  model?: string;
  platform?: string;
  platformVersion?: string;
  uaFullVersion?: string;
  wow64?: boolean;
}

interface NavigatorWithClientHints extends Navigator {
  deviceMemory?: number;
  userAgentData?: {
    mobile?: boolean;
    platform?: string;
    getHighEntropyValues?: (hints: string[]) => Promise<HighEntropyValues>;
  };
}

interface DeviceInfo {
  type: 'mobile' | 'tablet' | 'desktop';
  model: string;
  platform: string;
  platformVersion: string;
  architecture: string;
  mobile: boolean;
  userAgent: string;
  language: string;
  languages: readonly string[];
  deviceMemory: string;
  viewport: {
    width: number;
    height: number;
    pixelRatio: number;
  };
  screen: {
    width: number;
    height: number;
    colorDepth: number;
  };
  standalone: boolean;
}

interface AppLoggerBaseContext {
  angularVersion: string;
  build: AppLoggerBuildInfo;
  device: DeviceInfo;
  location: {
    href: string;
    referrer: string;
  };
}

interface ServiceWorkerStartupDiagnostics {
  supported: boolean;
  controller: {
    exists: boolean;
    scriptURL?: string;
    state?: ServiceWorkerState;
  };
  registration: {
    exists: boolean;
    scope?: string;
    updateViaCache?: ServiceWorkerUpdateViaCache;
    active?: ServiceWorkerVersionDiagnostics;
    waiting?: ServiceWorkerVersionDiagnostics;
    installing?: ServiceWorkerVersionDiagnostics;
  };
}

interface ServiceWorkerVersionDiagnostics {
  scriptURL: string;
  state: ServiceWorkerState;
}

@Injectable({ providedIn: 'root' })
export class AppLoggerService {
  private config = inject(APP_LOGGER_CONFIG, { optional: true }) ?? {};
  private readonly document = inject(DOCUMENT);
  private readonly lastEventAt = new Map<string, number>();
  private baseContext: AppLoggerBaseContext | undefined;
  private contextReady: Promise<AppLoggerBaseContext> | undefined;
  private currentUser: AppLoggerUser | null = null;
  private initialized = false;

  init(config?: AppLoggerConfig): void {
    this.config = config ?? this.config;

    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.baseContext = this.createBaseContext({});
    this.contextReady = this.readBaseContext()
      .then((context) => {
        this.baseContext = context;
        this.applyBaseContext();
        return context;
      })
      .catch((error: unknown) => {
        console.warn('App logger context failed', error);
        return this.baseContext ?? this.createBaseContext({});
      });

    const sentry = this.config.sentry;
    if (!sentry || sentry.enabled === false) {
      return;
    }

    Sentry.init({
      dsn: sentry.dsn,
      tunnel: sentry.tunnel,
      environment: sentry.environment,
      release: sentry.release,
      tracesSampleRate: sentry.tracesSampleRate ?? 0,
      sendDefaultPii: sentry.sendDefaultPii ?? false,
      beforeSend: (event) => {
        event.release ??= this.getEventRelease();
        return event;
      },
    });

    this.applyBaseContext();
  }

  debug(message: string, context?: AppLogContext): void {
    this.log('debug', message, context);
  }

  info(message: string, context?: AppLogContext): void {
    this.log('info', message, context);
  }

  warning(message: string, context?: AppLogContext): void {
    this.log('warning', message, context);
  }

  error(error: unknown, message?: string, context?: AppLogContext): void {
    this.captureException(error, { ...context, extra: { ...context?.extra, message } });
  }

  fatal(error: unknown, message?: string, context?: AppLogContext): void {
    this.captureException(error, { ...context, extra: { ...context?.extra, message } }, 'fatal');
  }

  log(level: AppLogLevel, message: string, context?: AppLogContext): void {
    const enrichedContext = this.withBaseTags(context);

    Sentry.addBreadcrumb({
      category: 'app',
      level: this.toSentryLevel(level),
      message,
      data: enrichedContext.extra,
    });

    if (!this.shouldCaptureEvent(level)) {
      this.writeConsole(level, message, enrichedContext, { captured: false, reason: 'below-minimum-level' });
      return;
    }

    if (this.shouldThrottleEvent(this.getEventKey(level, message, enrichedContext))) {
      this.writeConsole(level, message, enrichedContext, { captured: false, reason: 'cooldown' });
      return;
    }

    const eventId = this.withContext(enrichedContext, () => Sentry.captureMessage(message, this.toSentryLevel(level)));
    this.writeConsole(level, message, enrichedContext, { captured: this.isSentryEnabled(), eventId });
  }

  captureException(error: unknown, context?: AppLogContext, level: SeverityLevel = 'error'): void {
    const enrichedContext = this.withBaseTags(context);
    const appLevel = level === 'fatal' ? 'fatal' : 'error';
    if (
      this.shouldThrottleEvent(
        this.getEventKey(appLevel, this.getErrorSignature(error, enrichedContext), enrichedContext)
      )
    ) {
      this.writeConsole(appLevel, error, enrichedContext, { captured: false, reason: 'cooldown' });
      return;
    }

    const eventId = this.withContext(enrichedContext, () => Sentry.captureException(error, { level }));
    this.writeConsole(appLevel, error, enrichedContext, {
      captured: this.isSentryEnabled(),
      eventId,
    });
  }

  async emitStartupLog(): Promise<void> {
    if (!this.config.startupLog || this.config.startupLog.enabled === false) {
      return;
    }

    const [baseContext, serviceWorker] = await Promise.all([
      this.getStartupBaseContext(),
      this.getServiceWorkerStartupDiagnostics(),
    ]);

    this.info('app_opened', {
      extra: {
        ...baseContext,
        serviceWorker,
        pwaUpdateDiagnostics: {
          serviceWorkerSupported: serviceWorker.supported,
          serviceWorkerController: serviceWorker.controller.exists,
          serviceWorkerActive: serviceWorker.registration.active?.state ?? 'none',
          serviceWorkerWaiting: serviceWorker.registration.waiting?.state ?? 'none',
          serviceWorkerInstalling: serviceWorker.registration.installing?.state ?? 'none',
          serviceWorkerRegistrationScope: serviceWorker.registration.scope ?? 'none',
        },
      },
    });
  }

  private async getStartupBaseContext(): Promise<AppLoggerBaseContext> {
    const fallback = this.baseContext ?? this.createBaseContext({});
    if (!this.contextReady) {
      return fallback;
    }

    try {
      return await Promise.race([
        this.contextReady,
        this.wait(BASE_CONTEXT_STARTUP_TIMEOUT_MS).then(() => fallback),
      ]);
    } catch {
      return fallback;
    }
  }

  setUser(user: AppLoggerUser | null): void {
    this.currentUser = user;
    Sentry.setUser(user ? ({ ...user, userId: undefined } as User) : null);
    Sentry.setTag('user_id', this.getCurrentUserId());
  }

  setTag(key: string, value: AppLoggerTagValue): void {
    Sentry.setTag(key, value == null ? '' : String(value));
  }

  /**
   * Resolves once the asynchronous base context (build info + device client hints) has been
   * loaded and applied to the Sentry scope. Callers that emit early, low-frequency events
   * (e.g. the app-update lifecycle logs fired at bootstrap) can await this so their events
   * always carry the build tags/context instead of racing the `build-info.json` fetch.
   *
   * Resolves immediately once the context is ready, so awaiting it is free for events emitted
   * after startup (accepted/reload/etc.). If the logger was never initialized it also resolves
   * immediately, degrading to best-effort tagging rather than blocking.
   */
  async whenReady(): Promise<void> {
    await this.contextReady;
  }

  async flush(timeoutMs = 2000): Promise<boolean> {
    if (!this.isSentryEnabled()) {
      this.writeConsole('debug', 'app_logger_flush_skipped', undefined, {
        captured: false,
        reason: 'sentry-disabled',
      });
      return false;
    }

    const flushed = await Sentry.flush(timeoutMs);
    this.writeConsole('debug', 'app_logger_flush_completed', undefined, {
      captured: flushed,
      reason: flushed ? undefined : 'timeout-or-transport-failed',
    });
    return flushed;
  }

  isGlobalErrorCaptureEnabled(): boolean {
    return this.config.captureGlobalErrors === true;
  }

  private shouldCaptureEvent(level: AppLogLevel): boolean {
    const minimumLevel = this.config.minimumEventLevel ?? 'info';
    if (minimumLevel === 'off') {
      return false;
    }

    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[minimumLevel];
  }

  private shouldThrottleEvent(key: string): boolean {
    const cooldownMs = this.getEventCooldownMs();
    if (cooldownMs <= 0) {
      return false;
    }

    const now = Date.now();
    const last = this.lastEventAt.get(key) ?? 0;
    if (now - last < cooldownMs) {
      return true;
    }

    this.lastEventAt.set(key, now);
    return false;
  }

  private getEventCooldownMs(): number {
    return this.config.eventCooldownMs ?? 3000;
  }

  private getEventKey(level: AppLogLevel, message: string, context?: AppLogContext): string {
    return JSON.stringify({
      level,
      message,
      tags: context?.tags ? this.normalizeTags(context.tags) : {},
      fingerprint: context?.fingerprint ?? [],
    });
  }

  private getErrorSignature(error: unknown, context?: AppLogContext): string {
    const label = typeof context?.extra?.['message'] === 'string' ? context.extra['message'] : 'exception';

    if (error instanceof Error) {
      return `${label}:${error.name}:${error.message}`;
    }

    return `${label}:${String(error)}`;
  }

  private withBaseTags(context?: AppLogContext): AppLogContext {
    return {
      ...context,
      tags: {
        ...context?.tags,
        ...this.getBaseTags(),
      },
    };
  }

  private withContext<T>(context: AppLogContext | undefined, capture: () => T): T {
    if (!context) {
      return capture();
    }

    return Sentry.withScope((scope) => {
      if (context.tags) {
        scope.setTags(this.normalizeTags(context.tags));
      }

      if (context.extra) {
        scope.setContext('extra', context.extra);
      }

      if (context.user) {
        scope.setUser(context.user as User);
      }

      if (context.fingerprint) {
        scope.setFingerprint(context.fingerprint);
      }

      return capture();
    });
  }

  private applyBaseContext(): void {
    if (!this.baseContext) {
      return;
    }

    Sentry.setContext('app', {
      angularVersion: this.baseContext.angularVersion,
      location: this.baseContext.location,
    });
    Sentry.setContext('build', { ...this.baseContext.build });
    Sentry.setContext('device', { ...this.baseContext.device });
    Sentry.setTags(this.normalizeTags(this.getBaseTags()));
  }

  private async readBaseContext(): Promise<AppLoggerBaseContext> {
    const [build, highEntropyValues] = await Promise.all([this.readBuildInfo(), this.readHighEntropyValues()]);
    return this.createBaseContext(build, highEntropyValues);
  }

  private createBaseContext(
    build: AppLoggerBuildInfo,
    highEntropyValues: HighEntropyValues | null = null
  ): AppLoggerBaseContext {
    const nav = navigator as NavigatorWithClientHints;
    return {
      angularVersion: VERSION.full,
      build,
      device: this.getDeviceInfo(nav, highEntropyValues),
      location: {
        href: this.document.location.href,
        referrer: this.document.referrer || 'N/A',
      },
    };
  }

  private async readBuildInfo(): Promise<AppLoggerBuildInfo> {
    const buildInfoUrl = this.config.startupLog?.buildInfoUrl ?? 'build-info.json';
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), BUILD_INFO_TIMEOUT_MS)
      : undefined;

    try {
      const requestUrl = new URL(buildInfoUrl, this.document.baseURI).toString();
      const response = await fetch(requestUrl, { cache: 'no-store', signal: controller?.signal });
      return response.ok ? ((await response.json()) as AppLoggerBuildInfo) : {};
    } catch {
      return {};
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }
  }

  private async getServiceWorkerStartupDiagnostics(): Promise<ServiceWorkerStartupDiagnostics> {
    const fallback: ServiceWorkerStartupDiagnostics = {
      supported: false,
      controller: { exists: false },
      registration: { exists: false },
    };

    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return fallback;
    }

    const controller = navigator.serviceWorker.controller;
    const diagnostics: ServiceWorkerStartupDiagnostics = {
      supported: true,
      controller: {
        exists: controller !== null,
        scriptURL: controller?.scriptURL,
        state: controller?.state,
      },
      registration: { exists: false },
    };

    try {
      const registration = await Promise.race([
        navigator.serviceWorker.getRegistration(),
        this.wait(BASE_CONTEXT_STARTUP_TIMEOUT_MS).then(() => undefined),
      ]);

      if (!registration) {
        return diagnostics;
      }

      return {
        ...diagnostics,
        registration: {
          exists: true,
          scope: registration.scope,
          updateViaCache: registration.updateViaCache,
          active: this.getServiceWorkerVersionDiagnostics(registration.active),
          waiting: this.getServiceWorkerVersionDiagnostics(registration.waiting),
          installing: this.getServiceWorkerVersionDiagnostics(registration.installing),
        },
      };
    } catch {
      return diagnostics;
    }
  }

  private getServiceWorkerVersionDiagnostics(
    serviceWorker: ServiceWorker | null
  ): ServiceWorkerVersionDiagnostics | undefined {
    if (!serviceWorker) {
      return undefined;
    }

    return {
      scriptURL: serviceWorker.scriptURL,
      state: serviceWorker.state,
    };
  }

  private async wait(timeoutMs: number): Promise<void> {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, timeoutMs);
    });
  }

  private async readHighEntropyValues(): Promise<HighEntropyValues | null> {
    const nav = navigator as NavigatorWithClientHints;

    try {
      return (
        (await nav.userAgentData?.getHighEntropyValues?.([
          'architecture',
          'bitness',
          'fullVersionList',
          'model',
          'platform',
          'platformVersion',
          'uaFullVersion',
          'wow64',
        ])) ?? null
      );
    } catch {
      return null;
    }
  }

  // Browser/device capability collection is intentionally centralized so every logger event shares the same context.
  // eslint-disable-next-line complexity
  private getDeviceInfo(nav: NavigatorWithClientHints, highEntropyValues: HighEntropyValues | null): DeviceInfo {
    return {
      type: this.getDeviceType(nav),
      model: highEntropyValues?.model || 'N/A',
      platform: highEntropyValues?.platform || nav.userAgentData?.platform || nav.platform || 'N/A',
      platformVersion: highEntropyValues?.platformVersion || 'N/A',
      architecture: highEntropyValues?.architecture || 'N/A',
      mobile: highEntropyValues?.mobile ?? nav.userAgentData?.mobile ?? this.isMobileUserAgent(nav.userAgent),
      userAgent: nav.userAgent || 'N/A',
      language: nav.language || 'N/A',
      languages: nav.languages || [],
      deviceMemory: nav.deviceMemory ? `${nav.deviceMemory} GB` : 'N/A',
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        pixelRatio: window.devicePixelRatio || 1,
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth,
      },
      standalone:
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        (nav as Navigator & { standalone?: boolean }).standalone === true,
    };
  }

  private getBaseTags(): Record<string, AppLoggerTagValue> {
    return {
      app_build_hash: this.baseContext?.build.mainBundleHash,
      app_version: this.baseContext?.build.packageVersion,
      build_profile: this.baseContext?.build.buildProfile,
      build_id: this.baseContext?.build.buildId,
      device_model: this.baseContext?.device.model,
      device_platform: this.baseContext?.device.platform,
      device_type: this.baseContext?.device.type,
      git_sha: this.baseContext?.build.gitSha,
      user_id: this.getCurrentUserId(),
    };
  }

  private getEventRelease(): string | undefined {
    const build = this.baseContext?.build;
    const buildVersion = build?.mainBundleHash || build?.gitSha || build?.buildId;
    if (!buildVersion) {
      return undefined;
    }

    return `frontend-app@${build.buildProfile || this.config.sentry?.environment || 'unknown'}-${buildVersion}`;
  }

  private getCurrentUserId(): string {
    return this.currentUser?.userId || 'anonymous';
  }

  private getDeviceType(nav: NavigatorWithClientHints): 'mobile' | 'tablet' | 'desktop' {
    const userAgent = nav.userAgent || '';
    if (/ipad|tablet/i.test(userAgent)) return 'tablet';
    return nav.userAgentData?.mobile || this.isMobileUserAgent(userAgent) ? 'mobile' : 'desktop';
  }

  private isMobileUserAgent(userAgent: string): boolean {
    return /android|iphone|ipod|mobile/i.test(userAgent);
  }

  private normalizeTags(tags: NonNullable<AppLogContext['tags']>): Record<string, string> {
    return Object.entries(tags).reduce<Record<string, string>>((result, [key, value]) => {
      result[key] = value == null ? '' : String(value);
      return result;
    }, {});
  }

  private toSentryLevel(level: AppLogLevel): SeverityLevel {
    return level === 'warning' ? 'warning' : level;
  }

  private isSentryEnabled(): boolean {
    return !!this.config.sentry && this.config.sentry.enabled !== false;
  }

  private writeConsole(
    level: AppLogLevel,
    messageOrError: unknown,
    context?: AppLogContext,
    transport?: { captured: boolean; eventId?: string; reason?: string }
  ): void {
    if (!this.config.console) {
      return;
    }

    console.log('[app-logger]', {
      level,
      message: messageOrError,
      tags: context?.tags,
      extra: context?.extra,
      transport: {
        sentryEnabled: this.isSentryEnabled(),
        captured: transport?.captured ?? false,
        eventId: transport?.eventId,
        reason: transport?.reason,
        dsnHost: this.getDsnHost(),
        tunnel: this.config.sentry?.tunnel,
        environment: this.config.sentry?.environment,
      },
    });
  }

  private getDsnHost(): string | undefined {
    const dsn = this.config.sentry?.dsn;
    if (!dsn) {
      return undefined;
    }

    try {
      return new URL(dsn).host;
    } catch {
      return undefined;
    }
  }
}
